import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PostWithAuthor, Scholarship, Course, UserProfile, UserActivity, UserRole, CourseEnrollment, QuizAttempt, UserAchievement, Post, UserPreferences, UserLearningProgressData, Notification as AppNotification, FetchPostsParams } from '../types';
import {
  fetchUserProfileApi,
  createUserProfileApi,
  updateUserProfileApi,
  updateLastActiveApi,
  updateUserInterestsApi,
  changeUserRoleApi,
} from './user';
import {
  logUserActivityApi,
  fetchUserActivitiesApi,
} from './activity';
import {
  createNotificationApi,
  fetchUserNotificationsApi,
  markNotificationAsReadApi,
  deleteNotificationApi,
} from './notification';
import {
  fetchEnrolledCoursesApi,
  enrollInCourseApi,
  updateCourseEnrollmentApi,
  unenrollFromCourseApi,
  getCourses, // Added getCourses
} from './course';

import {
  fetchQuizAttemptsApi,
  recordQuizAttemptApi,
  deleteQuizAttemptApi,
} from './quiz';
import {
  addUserAchievementApi,
  fetchUserAchievementsApi,
  deleteUserAchievementApi,
} from './achievement';
import {
  fetchUserPostsApi,
} from './post';
import {
  syncLearningProgressApi,
  recordStudyTimeApi,
} from './learning';
import {
  updatePreferencesApi,
} from './preferences';
import {
  fetchPostsApi,
} from './blog';
import { fetchPaginatedScholarships } from './scholarship';
import { getCourseById } from './course'; // Assuming this is now axiosClient based



export const useScholarships = (filter?: { status?: string; page?: number; limit?: number }) => {
  return useQuery<Scholarship[], Error>({
    queryKey: ['scholarships', filter],
    queryFn: () => fetchPaginatedScholarships(filter?.page, filter?.limit, { status: filter?.status }).then(data => data.scholarships),
  });
};

export interface CourseFilter {
  category?: string;
  level?: string;
  priceRange?: [number, number];
  featured?: boolean;
  page?: number;
  limit?: number;
}

export const useCourses = (filter?: CourseFilter) => {
  return useQuery<{ courses: Course[]; totalPages: number }, Error>({
    queryKey: ['courses', filter],
    queryFn: () => getCourses({ ...filter, status: 'published' }), // Modified to use getCourses with status filter
  });
};

export const useCourseBySlug = (slug: string) => {
  return useQuery<Course | null, Error>({
    queryKey: ['course', slug],
    queryFn: () => getCourseById(slug), // Assuming getCourseById can handle slug or ID
    enabled: !!slug,
  });
};

// Optional: If you need to track lastVisible for pagination, create a separate function or hook
export const usePaginatedCourses = (filter: CourseFilter = {}) => {
  return useQuery<{
    courses: Course[];
    totalPages: number;
  }, Error>({
    queryKey: ['paginatedCourses', filter],
    queryFn: () => getCourses(filter),
  });
};

export const useUserProfileQuery = (uid: string) => {
  return useQuery<UserProfile | null, Error>({
    queryKey: ['userProfile', uid],
    queryFn: () => fetchUserProfileApi(uid),
    enabled: !!uid, // Only run if uid is available
  });
};

export const useCreateUserProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UserProfile, Error, { uid: string; profileData: Partial<UserProfile> }>({
    mutationFn: ({ uid, profileData }) => createUserProfileApi(uid, profileData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile', variables.uid] });
      // Optionally, update the local cache directly
      queryClient.setQueryData(['userProfile', variables.uid], data);
    },
  });
};

export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UserProfile, Error, { uid: string; profileUpdate: Partial<UserProfile> }>({
    mutationFn: ({ uid, profileUpdate }) => updateUserProfileApi(uid, profileUpdate),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile', variables.uid] });
      queryClient.setQueryData(['userProfile', variables.uid], data);
    },
  });
};

export const useUpdateLastActiveMutation = () => {
  return useMutation<void, Error, string>({
    mutationFn: (uid) => updateLastActiveApi(uid),
  });
};

export const useUpdateUserInterestsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UserProfile, Error, { uid: string; interests: string[] }>({
    mutationFn: ({ uid, interests }) => updateUserInterestsApi(uid, interests),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile', variables.uid] });
      queryClient.setQueryData(['userProfile', variables.uid], data);
    },
  });
};

export const useChangeUserRoleMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<boolean, Error, { userId: string; newRole: UserRole }>({
    mutationFn: ({ userId, newRole }) => changeUserRoleApi(userId, newRole),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['userProfile', variables.userId] });
      }
    },
  });
};

// --- User Activity React Query Hooks ---

export const useLogUserActivityMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<string, Error, { uid: string; activityData: Omit<UserActivity, "id"> }>({    mutationFn: ({ uid, activityData }) => logUserActivityApi({ uid, activityData }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userActivities', variables.uid] });
    },
  });
};

export const useUserActivitiesQuery = (uid: string) => {
  return useQuery<UserActivity[], Error>({
    queryKey: ['userActivities', uid],
    queryFn: () => fetchUserActivitiesApi(uid),
    enabled: !!uid,
  });
};

// --- Notification React Query Hooks ---

export const useCreateNotificationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<AppNotification, Error, Omit<AppNotification, "id">>({
    mutationFn: (notificationData) => createNotificationApi(notificationData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userNotifications', data.recipientId] });
    },
  });
};

export const useUserNotificationsQuery = (uid: string) => {
  return useQuery<AppNotification[], Error>({
    queryKey: ['userNotifications', uid],
    queryFn: () => fetchUserNotificationsApi(uid),
    enabled: !!uid,
  });
};

/**
 * Notification records carry `_id` from Mongo and the API also echoes it as
 * `id`; the cache updaters below compared against `_id` only, so they missed
 * whichever shape the list happened to hold.
 */
const idOf = (n: AppNotification): string =>
  String((n as { id?: string; _id?: string }).id ?? (n as { _id?: string })._id ?? '');

export const useMarkNotificationAsReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (notificationId) => markNotificationAsReadApi(notificationId),
    onSuccess: (_data, notificationId) => {
      /**
       * Two things were wrong here.
       *
       * The updater body was empty, so it returned `undefined` — which is not a
       * no-op to `setQueryData`, it *replaces* the cached value with nothing.
       * And it addressed `['userNotifications']` while the query registers
       * `['userNotifications', uid]`, so it never matched the real entry
       * anyway. `setQueriesData` matches on the key prefix, which is what both
       * of these mutations meant all along.
       */
      queryClient.setQueriesData<AppNotification[]>(
        { queryKey: ['userNotifications'] },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((n) =>
            idOf(n) === notificationId ? { ...n, read: true } : n
          );
        }
      );
    },
  });
};

export const useDeleteNotificationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (notificationId) => deleteNotificationApi(notificationId),
    onSuccess: (_data, notificationId) => {
      // Same prefix-matching fix as the mark-as-read mutation above.
      queryClient.setQueriesData<AppNotification[]>(
        { queryKey: ['userNotifications'] },
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.filter((n) => idOf(n) !== notificationId);
        }
      );
    },
  });
};

// --- Course Enrollment React Query Hooks ---

export const useEnrolledCoursesQuery = (uid: string) => {
  return useQuery<CourseEnrollment[], Error>({
    queryKey: ['enrolledCourses', uid],
    queryFn: () => fetchEnrolledCoursesApi(uid),
    enabled: !!uid,
  });
};

export const useEnrollInCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseEnrollment, Error, { uid: string; courseId: string; enrollmentType: 'paid' | 'scholarship' | 'free'; paymentReference?: string }>({
    mutationFn: ({ uid, courseId, enrollmentType, paymentReference }) => enrollInCourseApi(uid, courseId, enrollmentType, paymentReference),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrolledCourses', variables.uid] });
      // Optionally, update the local cache directly
      queryClient.setQueryData(['enrolledCourses', variables.uid], (oldData: CourseEnrollment[] | undefined) => {
        if (!oldData) return [data];
        return [...oldData, data];
      });
    },
  });
};

export const useUpdateCourseEnrollmentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<CourseEnrollment, Error, { enrollmentId: string; updates: Partial<CourseEnrollment> }>({
    mutationFn: ({ enrollmentId, updates }) => updateCourseEnrollmentApi(enrollmentId, updates),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrolledCourses'] }); // Invalidate all enrolled courses to be safe
      // Optimistically update the cache for the specific enrollment
      queryClient.setQueryData(['enrolledCourses', data.userId], (oldData: CourseEnrollment[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(enrollment => enrollment._id === variables.enrollmentId ? { ...enrollment, ...data } : enrollment);
      });
    },
  });
};

export const useUnenrollFromCourseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { uid: string; enrollmentId: string }>({
    mutationFn: ({ enrollmentId }) => unenrollFromCourseApi(enrollmentId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrolledCourses', variables.uid] });
      // Optimistically remove from cache
      queryClient.setQueryData(['enrolledCourses', variables.uid], (oldData: CourseEnrollment[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(enrollment => enrollment._id !== variables.enrollmentId);
      });
    },
  });
};

// --- Quiz React Query Hooks ---

export const useQuizAttemptsQuery = (uid: string) => {
  return useQuery<QuizAttempt[], Error>({
    queryKey: ['quizAttempts', uid],
    queryFn: () => fetchQuizAttemptsApi(uid),
    enabled: !!uid,
  });
};

export const useRecordQuizAttemptMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<QuizAttempt, Error, { uid: string; attemptData: Omit<QuizAttempt, "id" | "startedAt"> }>({    mutationFn: ({ uid, attemptData }) => recordQuizAttemptApi(uid, attemptData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quizAttempts', variables.uid] });
      // Optionally, update the local cache directly
      queryClient.setQueryData(['quizAttempts', variables.uid], (oldData: QuizAttempt[] | undefined) => {
        if (!oldData) return [data];
        return [...oldData, data];
      });
    },
  });
};

export const useDeleteQuizAttemptMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (attemptId) => deleteQuizAttemptApi(attemptId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quizAttempts'] }); // Invalidate all quiz attempts to be safe
      // Optimistically remove from cache
      queryClient.setQueryData(['quizAttempts'], (oldData: QuizAttempt[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(attempt => attempt.id !== variables);
      });
    },
  });
};

// --- Achievement React Query Hooks ---

export const useAddUserAchievementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UserAchievement, Error, { uid: string; achievementData: Omit<UserAchievement, "id" | "earnedAt"> }>({    mutationFn: ({ uid, achievementData }) => addUserAchievementApi(uid, achievementData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userAchievements', variables.uid] });
      // Optimistically update the cache
      queryClient.setQueryData(['userAchievements', variables.uid], (oldData: UserAchievement[] | undefined) => {
        if (!oldData) return [data];
        return [...oldData, data];
      });
    },
  });
};

export const useUserAchievementsQuery = (uid: string) => {
  return useQuery<UserAchievement[], Error>({
    queryKey: ['userAchievements', uid],
    queryFn: () => fetchUserAchievementsApi(uid),
    enabled: !!uid,
  });
};

export const useDeleteUserAchievementMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { uid: string; achievementId: string }>({    mutationFn: ({ uid, achievementId }) => deleteUserAchievementApi(uid, achievementId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userAchievements', variables.uid] });
      // Optimistically remove from cache
      queryClient.setQueryData(['userAchievements', variables.uid], (oldData: UserAchievement[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(achievement => achievement.id !== variables.achievementId);
      });
    },
  });
};

// --- Post React Query Hooks ---

export const useUserPostsQuery = (uid: string) => {
  return useQuery<Post[], Error>({
    queryKey: ['userPosts', uid],
    queryFn: () => fetchUserPostsApi(uid),
    enabled: !!uid,
  });
};

export const usePosts = (params?: FetchPostsParams) => {
  return useQuery<{ posts: PostWithAuthor[]; total: number }, Error>({
    queryKey: ['posts', params],
    queryFn: () => fetchPostsApi(params),
  });
};

// --- Learning React Query Hooks ---

export const useSyncLearningProgressMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { uid: string; enrollmentId: string; progressData: UserLearningProgressData}>({    mutationFn: ({ uid, enrollmentId, progressData }) => syncLearningProgressApi(uid, enrollmentId, progressData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enrolledCourses', variables.uid] }); // Invalidate enrolled courses to reflect progress
    },
  });
};

export const useRecordStudyTimeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { uid: string; minutes: number }>({    mutationFn: ({ uid, minutes }) => recordStudyTimeApi(uid, minutes),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile', variables.uid] }); // Invalidate user profile to reflect study hours
      queryClient.invalidateQueries({ queryKey: ['userActivities', variables.uid] }); // Invalidate activities for study session log
    },
  });
};

// --- Preferences React Query Hooks ---

export const useUpdatePreferencesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UserPreferences, Error, { uid: string; preferences: Partial<UserPreferences> }>({    mutationFn: ({ uid, preferences }) => updatePreferencesApi(uid, preferences),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile', variables.uid] }); // Invalidate user profile to reflect preferences
    },
  });
};

